// Tiny chat-flavored markdown renderer — pure React nodes, no HTML injection.
// Supports: ```code blocks```, `inline code`, **bold**, _italic_, __underline__,
// ~strike~, > quotes, - bullets, 1. numbered lists, bare URLs, :shortcodes:,
// #hex color swatches, and inline images from linked URLs.
import React from 'react';

export type MdOpts = {
  colorSwatches?: boolean; // show a color chip next to #rrggbb values
  emojiPlain?: boolean; // render :shortcodes: literally instead of as emoji
  linkMedia?: boolean; // inline an image when a message links directly to one
  // Auto-linking: resolve #channel and @person mentions to clickable links.
  channels?: { id: string; name?: string }[];
  members?: { id: string; name?: string; email?: string }[];
  onChannel?: (id: string) => void;
  onMention?: (id: string) => void;
};

const SHORTCODES: Record<string, string> = {
  smile: '😄', grin: '😁', joy: '😂', wink: '😉', heart: '❤️', fire: '🔥',
  rocket: '🚀', tada: '🎉', eyes: '👀', check: '✅', white_check_mark: '✅',
  thumbsup: '👍', '+1': '👍', thumbsdown: '👎', '-1': '👎', pray: '🙏',
  clap: '👏', raised_hands: '🙌', bulb: '💡', warning: '⚠️', zap: '⚡',
  bug: '🐛', ship: '🚢', sob: '😭', thinking: '🤔', robot: '🤖', wave: '👋',
  '100': '💯', muscle: '💪', ok_hand: '👌', point_up: '☝️',
};

const IMG_EXT = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?\S*)?$/i;

const MENTION: React.CSSProperties = {
  background: 'rgba(247,181,0,0.16)', color: 'var(--fx-accent, #f7b500)', border: 'none',
  borderRadius: 5, padding: '0 4px', cursor: 'pointer', font: 'inherit', fontWeight: 600,
};

const INLINE =
  /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(_[^_]+_)|(~[^~]+~)|(:[a-z0-9_+-]+:)|(#[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?\b)|(https?:\/\/[^\s<>]+)|((?<![\w@.-])@[a-zA-Z][\w.-]*)|(#[a-zA-Z][\w-]*)/g;

function inline(text: string, keyBase: string, opts: MdOpts): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index ?? 0;
    if (idx < last) continue; // consumed by a widened mention match
    if (idx > last) out.push(text.slice(last, idx));
    let tok = m[0];
    const k = `${keyBase}-${i++}`;
    if (tok.startsWith('`')) {
      out.push(<code className="fx-md-code" key={k}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('**')) {
      out.push(<b key={k}>{inline(tok.slice(2, -2), k, opts)}</b>);
    } else if (tok.startsWith('__')) {
      out.push(<u key={k}>{inline(tok.slice(2, -2), k, opts)}</u>);
    } else if (tok.startsWith('_')) {
      out.push(<i key={k}>{inline(tok.slice(1, -1), k, opts)}</i>);
    } else if (tok.startsWith('~')) {
      out.push(<s key={k}>{inline(tok.slice(1, -1), k, opts)}</s>);
    } else if (tok.startsWith(':') && tok.endsWith(':')) {
      const name = tok.slice(1, -1);
      const emoji = SHORTCODES[name];
      out.push(emoji && !opts.emojiPlain ? emoji : tok);
    } else if (tok.startsWith('@') && /^(team)\b/i.test(text.slice(idx + 1))) {
      // Group mention — highlighted like a personal mention, not clickable.
      const g = /^(team)/i.exec(text.slice(idx + 1))!;
      tok = text.slice(idx, idx + 1 + g[1].length);
      out.push(<span key={k} style={MENTION}>{tok}</span>);
    } else if (tok.startsWith('@')) {
      // Names are multi-word ("@Arjun Desai") but the regex stops at the first
      // space — widen the token to the LONGEST member name that matches the
      // text at this position, then fall back to first-name/email matching.
      const after = text.slice(idx + 1).toLowerCase();
      let mem: { id: string; name?: string; email?: string } | undefined;
      let width = 0;
      for (const mm of opts.members || []) {
        const nm = (mm.name || '').toLowerCase();
        if (nm && after.startsWith(nm) && nm.length > width) { mem = mm; width = nm.length; }
      }
      if (mem) {
        tok = text.slice(idx, idx + 1 + width);
      } else {
        const t = tok.slice(1).toLowerCase();
        mem = opts.members?.find((mm) => {
          const nm = (mm.name || '').toLowerCase();
          return nm.replace(/\s+/g, '') === t || nm.split(/\s+/)[0] === t
            || (mm.email || '').split('@')[0].toLowerCase() === t;
        });
      }
      out.push(mem && opts.onMention
        ? <button type="button" key={k} style={MENTION} onClick={() => opts.onMention!(mem!.id)}>{tok}</button>
        : tok);
    } else if (tok.startsWith('#')) {
      const chName = tok.slice(1).toLowerCase();
      const ch = opts.channels?.find((c) => (c.name || '').toLowerCase() === chName);
      if (ch && opts.onChannel) {
        out.push(<button type="button" key={k} style={MENTION} onClick={() => opts.onChannel!(ch.id)}>#{ch.name}</button>);
      } else {
        const hex = tok.length === 4
          ? `#${tok[1]}${tok[1]}${tok[2]}${tok[2]}${tok[3]}${tok[3]}`
          : tok;
        out.push(
          opts.colorSwatches ? (
            <span className="fx-hexcode" key={k}>
              <span className="fx-hex-swatch" style={{ background: hex }} />
              {tok}
            </span>
          ) : (
            tok
          ),
        );
      }
    } else {
      // bare URL — inline the image if it links directly to one and media is on
      if (opts.linkMedia && IMG_EXT.test(tok)) {
        out.push(
          <a className="fx-md-imglink" key={k} href={tok} target="_blank" rel="noreferrer">
            <img className="fx-image" src={tok} alt={tok} loading="lazy" />
          </a>,
        );
      } else {
        out.push(
          <a className="fx-md-link" key={k} href={tok} target="_blank" rel="noreferrer">{tok}</a>,
        );
      }
    }
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function textBlocks(seg: string, keyBase: string, opts: MdOpts): React.ReactNode[] {
  const lines = seg.split('\n');
  const out: React.ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^>\s?/.test(line)) {
      const q: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) q.push(lines[i].replace(/^>\s?/, '')), i++;
      out.push(
        <blockquote className="fx-md-quote" key={`${keyBase}-q${k++}`}>
          {q.map((l, j) => <div key={j}>{inline(l, `${keyBase}-q${k}-${j}`, opts)}</div>)}
        </blockquote>,
      );
    } else if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) items.push(lines[i].replace(/^[-*]\s+/, '')), i++;
      out.push(
        <ul className="fx-md-list" key={`${keyBase}-u${k++}`}>
          {items.map((l, j) => <li key={j}>{inline(l, `${keyBase}-u${k}-${j}`, opts)}</li>)}
        </ul>,
      );
    } else if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) items.push(lines[i].replace(/^\d+\.\s+/, '')), i++;
      out.push(
        <ol className="fx-md-list" key={`${keyBase}-o${k++}`}>
          {items.map((l, j) => <li key={j}>{inline(l, `${keyBase}-o${k}-${j}`, opts)}</li>)}
        </ol>,
      );
    } else if (
      /^\|.*\|\s*$/.test(line) &&
      i + 1 < lines.length &&
      /^\|(?:\s*:?-+:?\s*\|)+\s*$/.test(lines[i + 1])
    ) {
      // GFM table: header row, |---|---| separator, then body rows.
      const row = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const header = row(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\|.*\|\s*$/.test(lines[i])) { rows.push(row(lines[i])); i++; }
      out.push(
        // The wrapper owns horizontal scroll so a wide table never scrolls the page.
        <div className="fx-md-tablewrap" key={`${keyBase}-tb${k++}`}>
          <table className="fx-md-table">
            <thead><tr>{header.map((c, j) => <th key={j}>{inline(c, `${keyBase}-th${k}-${j}`, opts)}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, j) => <td key={j}>{inline(c, `${keyBase}-td${k}-${ri}-${j}`, opts)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
    } else {
      out.push(
        <React.Fragment key={`${keyBase}-t${k++}`}>
          {inline(line, `${keyBase}-t${k}`, opts)}
          {i < lines.length - 1 ? '\n' : null}
        </React.Fragment>,
      );
      i++;
    }
  }
  return out;
}

/** Render a message body. Falls back to plain text when nothing matches. */
export function renderMd(body: string, opts: MdOpts = {}): React.ReactNode {
  if (!body) return null;
  const parts = body.split(/```/);
  const trivial =
    parts.length === 1 &&
    !/[*_~`>|]|https?:\/\/|^[-*\d]|:[a-z0-9_+-]+:|#[0-9a-fA-F]{3}|[@#][a-zA-Z]/m.test(body);
  if (trivial) return body;
  return parts.map((seg, i) =>
    i % 2 === 1 ? (
      // eslint-disable-next-line react/no-array-index-key
      <pre className="fx-md-pre" key={i}>{seg.replace(/^\n|\n$/g, '')}</pre>
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={i}>{textBlocks(seg, `s${i}`, opts)}</React.Fragment>
    ),
  );
}
